import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRightIcon, LightningIcon } from "@phosphor-icons/react";
import Header from "@/components/Header";
import { track } from "@/lib/analytics";

const BEFORE_IMG = "/demo-before.jpg";
const AFTER_IMG = "/demo-after.jpg";

export default function Landing() {
    return (
        <div className="min-h-screen bg-[#0A0A0C] text-white font-cn">
            <Header />

            {/* Hero */}
            <section className="relative overflow-hidden grain">
                <div
                    className="absolute inset-0 pointer-events-none"
                    style={{
                        background:
                            "radial-gradient(60% 50% at 50% 10%, rgba(255,255,255,0.05) 0%, rgba(10,10,12,1) 70%)",
                    }}
                />

                <div className="relative max-w-3xl mx-auto px-5 sm:px-8 pt-10 sm:pt-20 pb-12 sm:pb-20">
                    <motion.div
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="text-center"
                    >
                        <h1
                            className="font-cn font-bold tracking-tight text-white leading-[1.08]"
                            style={{
                                fontSize: "clamp(38px, 11vw, 78px)",
                            }}
                            data-testid="hero-headline"
                        >
                            照片不帅
                            <br />
                            <span className="text-white/45">辣妹不爱</span>
                        </h1>

                        <div className="mt-7 sm:mt-9 flex flex-col items-center gap-3">
                            <Link
                                to="/upload"
                                onClick={() => track("cta_hero_upload")}
                                data-testid="hero-upload-cta"
                                className="group inline-flex items-center justify-center gap-2 bg-white text-black font-bold rounded-full px-8 py-5 text-lg sm:text-xl shadow-[0_8px_30px_rgba(255,255,255,0.18)] hover:bg-white/90 active:scale-[0.98] transition-all w-full sm:w-auto"
                            >
                                <LightningIcon size={20} weight="fill" />
                                上传照片，30秒出结果
                                <ArrowRightIcon
                                    size={18}
                                    weight="bold"
                                    className="group-hover:translate-x-0.5 transition-transform"
                                />
                            </Link>
                            <p
                                className="text-sm text-white/55"
                                data-testid="hero-free-line"
                            >
                                免费看看你能变多帅｜首张照片免费
                            </p>
                        </div>
                    </motion.div>

                    {/* Before / After — unobstructed */}
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.1 }}
                        className="mt-10 sm:mt-14"
                        data-testid="demo-before-after"
                    >
                        <div className="grid grid-cols-2 gap-3 sm:gap-4">
                            <figure
                                className="space-y-2"
                                data-testid="demo-before-figure"
                            >
                                <div className="aspect-[4/5] rounded-2xl overflow-hidden bg-[#141417] border border-white/5">
                                    <img
                                        src={BEFORE_IMG}
                                        alt="Before"
                                        className="w-full h-full object-cover"
                                        draggable={false}
                                    />
                                </div>
                                <figcaption className="text-xs font-medium text-white/45 text-center">
                                    之前
                                </figcaption>
                            </figure>
                            <figure
                                className="space-y-2"
                                data-testid="demo-after-figure"
                            >
                                <div className="aspect-[4/5] rounded-2xl overflow-hidden bg-[#141417] border border-white/20 ring-1 ring-white/10">
                                    <img
                                        src={AFTER_IMG}
                                        alt="After"
                                        className="w-full h-full object-cover"
                                        draggable={false}
                                    />
                                </div>
                                <figcaption className="text-xs font-semibold text-white text-center">
                                    之后
                                </figcaption>
                            </figure>
                        </div>
                    </motion.div>

                    {/* Second upload CTA below before/after */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.25, duration: 0.5 }}
                        className="mt-8 sm:mt-12 flex flex-col items-center"
                    >
                        <Link
                            to="/upload"
                            onClick={() => track("cta_below_demo_upload")}
                            data-testid="below-demo-upload-cta"
                            className="inline-flex items-center justify-center gap-2 bg-white text-black font-bold rounded-full px-7 py-4 text-base hover:bg-white/90 active:scale-[0.98] transition-all w-full sm:w-auto"
                        >
                            <LightningIcon size={18} weight="fill" />
                            上传照片，30秒出结果
                        </Link>
                        <p className="mt-2 text-xs text-white/40">
                            30秒｜无需付款｜匿名上传
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* 3 short value props in Chinese */}
            <section className="max-w-3xl mx-auto px-5 sm:px-8 pb-16 sm:pb-24">
                <div className="grid grid-cols-3 gap-3 sm:gap-4">
                    {[
                        { big: "30秒", small: "出结果" },
                        { big: "无需付款", small: "首张免费" },
                        { big: "100%", small: "你本人" },
                    ].map((it) => (
                        <div
                            key={it.big}
                            className="rounded-2xl bg-white/[0.04] border border-white/10 p-4 sm:p-5 text-center"
                        >
                            <div className="font-cn font-bold text-xl sm:text-2xl text-white">
                                {it.big}
                            </div>
                            <div className="text-xs text-white/50 mt-1">
                                {it.small}
                            </div>
                        </div>
                    ))}
                </div>
            </section>
        </div>
    );
}
